import { Navigate, createBrowserRouter } from 'react-router-dom';
import { Shell } from '@/components/layout/Shell';
import { LoginPage } from '@/modules/auth/LoginPage';
import { DashboardPage } from '@/modules/dashboard/DashboardPage';
import { PlaceholderPage } from '@/modules/_placeholder/PlaceholderPage';
import { PresupuestoEgresosPage } from '@/modules/presupuesto/PresupuestoEgresosPage';
import { PolizasPage } from '@/modules/contabilidad/PolizasPage';
import { CxPPage } from '@/modules/cxp/CxPPage';
import { BancosPage } from '@/modules/bancos/BancosPage';
import { ProveedoresPage } from '@/modules/proveedores/ProveedoresPage';
import { ProveedorDetallePage } from '@/modules/proveedores/ProveedorDetallePage';
import { ArticulosPage } from '@/modules/articulos/ArticulosPage';
import { ArticuloDetallePage } from '@/modules/articulos/ArticuloDetallePage';
import { AlmacenPage } from '@/modules/almacen/AlmacenPage';
import { BienesPage } from '@/modules/bienes/BienesPage';
import { BienDetallePage } from '@/modules/bienes/BienDetallePage';
import { DepreciacionPage } from '@/modules/bienes/DepreciacionPage';
import { PatrimonioPage } from '@/modules/patrimonio/PatrimonioPage';
import { RequisicionesPage } from '@/modules/adquisiciones/RequisicionesPage';
import { RequisicionDetallePage } from '@/modules/adquisiciones/RequisicionDetallePage';
import { OrdenesCompraPage } from '@/modules/adquisiciones/OrdenesCompraPage';
import { NuevaOCPage } from '@/modules/adquisiciones/NuevaOCPage';
import { OCDetallePage } from '@/modules/adquisiciones/OCDetallePage';
import { CotizacionesPage } from '@/modules/adquisiciones/CotizacionesPage';
import { CotizacionDetallePage } from '@/modules/adquisiciones/CotizacionDetallePage';
import { ContratosPage, ContratoDetallePage } from '@/modules/adquisiciones/ContratosPage';
import { PAAPage } from '@/modules/adquisiciones/PAAPage';
import { ObrasPage } from '@/modules/obra/ObrasPage';
import { ObraDetallePage, ContratoObraDetallePage } from '@/modules/obra/ObraDetallePage';
import { ConteoFisicoListPage, ConteoFisicoDetallePage } from '@/modules/almacen/ConteoFisicoPage';
import { LicitacionesPage, LicitacionDetallePage } from '@/modules/adquisiciones/LicitacionesPage';
import { InversionPage } from '@/modules/adquisiciones/InversionPage';
import { AlertasPage } from '@/modules/alertas/AlertasPage';
import { useAuth } from '@/lib/authStore';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuth((s) => s.token);
  const expiraEn = useAuth((s) => s.expiraEn);
  if (!token) return <Navigate to="/login" replace />;
  if (expiraEn && new Date(expiraEn) < new Date()) {
    useAuth.getState().clear();
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function RedirectIfAuth({ children }: { children: React.ReactNode }) {
  const token = useAuth((s) => s.token);
  if (token) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

export const router = createBrowserRouter([
  { path: '/', element: <Navigate to="/app" replace /> },
  { path: '/login', element: <RedirectIfAuth><LoginPage /></RedirectIfAuth> },
  {
    path: '/app',
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardPage /> },

      // Financieros (F1.0)
      { path: 'presupuesto/egresos',  element: <PresupuestoEgresosPage /> },
      { path: 'contabilidad/polizas', element: <PolizasPage /> },
      { path: 'cxp',                  element: <CxPPage /> },
      { path: 'bancos',               element: <BancosPage /> },

      // Materiales
      { path: 'proveedores',     element: <ProveedoresPage /> },
      { path: 'proveedores/:id', element: <ProveedorDetallePage /> },
      { path: 'almacen',       element: <AlmacenPage /> },
      { path: 'articulos',     element: <ArticulosPage /> },
      { path: 'articulos/:id', element: <ArticuloDetallePage /> },
      { path: 'bienes',        element: <BienesPage /> },
      { path: 'bienes/:id',    element: <BienDetallePage /> },
      { path: 'depreciacion',  element: <DepreciacionPage /> },
      { path: 'patrimonio',    element: <PatrimonioPage /> },
      { path: 'adquisiciones',       element: <Navigate to="/app/requisiciones" replace /> },
      { path: 'requisiciones',       element: <RequisicionesPage /> },
      { path: 'requisiciones/:id',   element: <RequisicionDetallePage /> },
      { path: 'ordenes-compra',       element: <OrdenesCompraPage /> },
      { path: 'ordenes-compra/nueva', element: <NuevaOCPage /> },
      { path: 'ordenes-compra/:id',   element: <OCDetallePage /> },
      { path: 'cotizaciones',         element: <CotizacionesPage /> },
      { path: 'cotizaciones/:id',     element: <CotizacionDetallePage /> },
      { path: 'contratos',            element: <ContratosPage /> },
      { path: 'contratos/:id',        element: <ContratoDetallePage /> },
      { path: 'paa',                  element: <PAAPage /> },
      { path: 'obras',                        element: <ObrasPage /> },
      { path: 'obras/:id',                    element: <ObraDetallePage /> },
      { path: 'obras/:id/contratos/:coId',    element: <ContratoObraDetallePage /> },
      { path: 'obra',          element: <Navigate to="/app/obras" replace /> },
      { path: 'conteos',                  element: <ConteoFisicoListPage /> },
      { path: 'conteos/:id',              element: <ConteoFisicoDetallePage /> },
      { path: 'licitaciones',             element: <LicitacionesPage /> },
      { path: 'licitaciones/:id',         element: <LicitacionDetallePage /> },
      { path: 'inversion',                element: <InversionPage /> },
      { path: 'alertas',       element: <AlertasPage /> },

      { path: 'configuracion', element: <PlaceholderPage title="Configuración" description="Ente público, unidades administrativas, catálogos, usuarios, roles." /> },
    ],
  },
  { path: '*', element: <Navigate to="/app" replace /> },
]);
